import { REQUIRED_MESSAGE } from "@/utils/constant";
import { generateParams } from "@/utils/helper/generateParams";
import { axiosInstance } from "@/utils/lib/axios";
import { swalError } from "@/utils/lib/swal";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

const mediaTempPayloadSchema = z.object({
  file: z.instanceof(File, { message: REQUIRED_MESSAGE }),
  target: z.string().min(1, { message: REQUIRED_MESSAGE }),
  entityId: z.string().optional(),
  className: z.string().optional(),
  type: z.string().optional(),
  properties: z.string().optional(),
});

type MediaTempPayloadSchema = z.infer<typeof mediaTempPayloadSchema>;

interface MediaData {
  id: string;
  path: string;
  originalFileName: string;
  name: string;
  type?: string;
  search?: string;
  stream?: string;
  download?: string;
  status?: boolean;
}

/* ============ *
 * CREATE media *
 * ============ */

async function createMediaTemp(
  data: MediaTempPayloadSchema,
  uploadApiUrl?: string,
): Promise<MediaData> {
  const path = uploadApiUrl ?? "/api/crud/media/upload/temp";
  const response = await axiosInstance.postForm(path, data);
  return response.data.data;
}

function useMediaTempCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      uploadApiUrl,
    }: {
      data: MediaTempPayloadSchema;
      uploadApiUrl?: string;
    }) => createMediaTemp(data, uploadApiUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medias"] });
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        swalError.fire({
          text: "The size of the data is too big",
        });
      }
    },
  });
}

/* ========== *
 * READ media *
 * ========== */

interface MediaDownloadTarget {
  path: string;
  fileName: string;
}

interface MediaQueryParams {
  [key: string]: string | number | boolean | undefined;
}

async function getMedias(
  queryFunctionContext: QueryFunctionContext,
): Promise<MediaData[]> {
  const { queryKey } = queryFunctionContext;
  const { params } = queryKey[1] as { params: MediaQueryParams };

  const response = await axiosInstance.get(
    `/api/crud/media${generateParams(params)}`,
  );

  return response.data.data.content;
}

function useMedias(params: MediaQueryParams, options?: { enabled?: boolean }) {
  return useQuery({
    ...options,
    queryKey: ["medias", { params }],
    queryFn: getMedias,
  });
}

async function downloadMedia(target: MediaDownloadTarget): Promise<void> {
  const { path, fileName } = target;
  const response = await axiosInstance.get(
    `/api/crud/media/download?path=${encodeURIComponent(
      path,
    )}&fileName=${encodeURIComponent(fileName)}`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
// Using mutation for simplicity in downloading file.
// Ref: https://github.com/TanStack/query/discussions/3105#discussioncomment-1833920
function useMediaDownload() {
  return useMutation({
    mutationFn: (target: MediaDownloadTarget) => downloadMedia(target),
  });
}

/* ================= *
 * CREATE media temp *
 * ================= */

async function createBpmnMediaTemp(
  data: MediaTempPayloadSchema,
): Promise<MediaData> {
  const response = await axiosInstance.postForm("/bpmn/Rfs/upload/temp", data);
  return response.data.data;
}
function useBpmnMediaTempCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: MediaTempPayloadSchema }) =>
      createBpmnMediaTemp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bpmn-medias"] });
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        swalError.fire({
          text: "The size of the data is too big",
        });
      }
    },
  });
}

/* ============== *
 * READ media uri *
 * ============== */

async function getMediaUri(mediaPath?: string): Promise<MediaData> {
  const response = await axiosInstance.get(
    `/api/crud/media/uri?path=${encodeURIComponent(mediaPath ?? "")}`,
  );
  return response.data.data;
}
function useMediaUri(
  mediaPath?: string,
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["media-uri", mediaPath],
    queryFn: () => getMediaUri(mediaPath),
    staleTime: 1000 * 60 * 10,
  });
}

/* ============== *
 * DOWNLOAD media *
 * ============== */

async function downloadMediaUri(mediaPath?: string): Promise<void> {
  const response = await axiosInstance.get(
    `/api/crud/media/uri?path=${encodeURIComponent(mediaPath ?? "")}`,
  );

  const downloadUrl = response.data.data.download;
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", "");
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadMediaUri() {
  return useMutation({
    mutationFn: (mediaPath?: string) => downloadMediaUri(mediaPath),
  });
}

/* ============== *
 * DOWNLOAD media uri with filename *
 * ============== */

async function downloadMediaUriFilename(
  target: MediaDownloadTarget,
): Promise<void> {
  const { path, fileName } = target;
  const response = await axiosInstance.get(
    `/api/crud/media/uri?path=${encodeURIComponent(
      path,
    )}&fileName=${encodeURIComponent(fileName)}`,
  );

  const downloadUrl = response.data.data.download;
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadMediaUriFilename() {
  return useMutation({
    mutationFn: (target: MediaDownloadTarget) =>
      downloadMediaUriFilename(target),
  });
}

export type { MediaData, MediaTempPayloadSchema, MediaDownloadTarget };
export {
  useMediaTempCreate,
  useMediaDownload,
  useBpmnMediaTempCreate,
  useMediaUri,
  useDownloadMediaUri,
  useDownloadMediaUriFilename,
  useMedias,
};
